import { useCallback, useMemo } from 'react';
import { useWeb3React } from '@web3-react/core';
import _ from 'lodash';
import DataTable from 'react-data-table-component';

import useRaffles from '../hooks/useRaffles';
import useRaffleParticipants from '../hooks/useRaffleParticipants';
import useDisplayNames from '../hooks/useDisplayName';
import { Spinner } from 'react-bootstrap';

// const formatAccount = (account) =>
//   `${account.substring(0, 5)}...${account.substring(account.length - 4)}`;

const RaffleParticipantsTab = ({ raffleKey }) => {
  const { account } = useWeb3React();

  const raffle = useRaffles(raffleKey);
  const { participants, ready } = useRaffleParticipants(raffleKey);

  const rows = useMemo(
    () =>
      _(participants)
        .entries()
        .map(([account, ticketCount]) => ({ account, ticketCount }))
        .sortBy('ticketCount')
        .reverse()
        .map((row, index) => ({ ...row, index: index + 1 }))
        .value(),
    [participants]
  );

  const accountIndex = _.findIndex(rows, (row) => row.account === account);

  const calculateOdds = useCallback(
    (account) => {
      const totalTicketCount = _.sum(_.values(participants));
      const ticketCount = participants[account];

      return (
        (1 -
          Math.pow(
            (totalTicketCount - ticketCount) / totalTicketCount,
            raffle?.winners || 1
          )) *
        100
      );
    },
    [participants, raffle]
  );

  const displayName = useDisplayNames();

  if (!account) {
    return (
      <div className="alert alert-info">
        Connect your wallet to view the raffle participants.
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="text-center">
        <p>Loading participants...</p>
        <div>
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  const columns = [
    {
      name: '',
      selector: (row) => row.index,
      right: true,
    },
    {
      name: 'Account',
      selector: (row) => row.account,
      format: (row) => displayName(row.account),
      grow: 8,
    },
    {
      name: 'Tickets',
      selector: (row) => row.ticketCount,
      right: true,
    },
    {
      name: 'Odds',
      selector: (row) => calculateOdds(row.account),
      format: (row) => `${calculateOdds(row.account).toFixed(1)}%`,
      right: true,
    },
  ];

  const rowCount = 10;
  const data = rows.slice(0, rowCount);
  if (account && participants[account] > 0 && accountIndex >= rowCount) {
    data.push({
      index: accountIndex + 1,
      account,
      ticketCount: participants[account],
    });
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        keyField="account"
        striped
        customStyles={{
          tableWrapper: {
            style: {
              display: 'flex',
            },
          },
          head: {
            style: {
              fontSize: '1rem',
              fontWeight: 'bold',
            },
          },
          rows: {
            style: {
              fontSize: '1rem',
            },
          },
        }}
        conditionalRowStyles={[
          {
            when: (row) => row.account === account,
            classNames: ['bg-info fw-bold'],
          },
        ]}
      />
    </>
  );
};

export default RaffleParticipantsTab;
